"use client";

import { useAuth, useUser } from '@clerk/nextjs';
import { uploadFile } from '@/lib/upload';
import { toast } from 'sonner';
import type { PropsWithChildren } from 'react';
import { useProject } from '@/providers/project';
import { openPinturaImageModal } from '@/components/nodes/pintura/open';
import { useI18n } from '@/providers/i18n';

type Props = PropsWithChildren<{
  src: string;
  disabled?: boolean;
  filenamePrefix?: string;
  cropAspectRatio?: number | string;
  className?: string;
  onUpdated: (result: { url: string; type: string; width?: number; height?: number; size?: string }) => void;
  onUploadingChange?: (uploading: boolean) => void;
}>;

export const EditWithPinturaOnDoubleClick = ({
  children,
  src,
  disabled,
  filenamePrefix = 'edit',
  cropAspectRatio,
  className,
  onUpdated,
  onUploadingChange,
}: Props) => {
  const { getToken } = useAuth();
  const { user } = useUser();
  const project = useProject();
  const { t } = useI18n();

  const handleDoubleClick = async () => {
    if (disabled) return;
    try {
      const edited = await openPinturaImageModal({ src, cropAspectRatio });
      if (!edited) return;

      onUploadingChange?.(true);
      const token = await getToken({ template: 'supabase' });
      if (!token || !user?.id) {
        toast.error(t('Please sign in to upload'));
        onUploadingChange?.(false);
        return;
      }

      const file = new File([edited.blob], `${filenamePrefix}-${Date.now()}.png`, { type: 'image/png' });
      const { url, type } = await uploadFile(file, 'files', {
        token,
        userId: user.id,
        projectId: String(project?.id || ''),
        assetType: 'image',
      });
      onUpdated({ url, type, width: edited.width, height: edited.height, size: edited.size });
      toast.success(t('Image updated'));
    } catch (e) {
      console.error(e);
      toast.error(t('Failed to edit image'));
    } finally {
      onUploadingChange?.(false);
    }
  };

  return (
    <div className={className} onDoubleClick={handleDoubleClick}>
      {children}
    </div>
  );
};


